import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { message, Space, Tooltip, Input, Button } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ImportOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import PlusIcon from "../../../assets/img/plusButtton.png";
import "./index.css";
import AddDescriptorSetModal from "./AddDescriptorSetModal";
import AddFieldModal from "./AddFieldModal";
import AddDescriptorModal from "./AddDescriptorModal";
import GenericTable from "../../GeneralTable/GenericTable";
import type { ColumnsType } from "antd/es/table";
import AppModal from "../../modals/AppModal";
import { useParams } from "react-router-dom";
import { useExporter } from "../../../hooks/useExporter";

// redux
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setProjectId,
  // sets
  fetchSets,
  openSetModal,
  closeSetModal,
  setEditingSet,
  openDeleteSet,
  closeDeleteSet,
  deleteSet as thunkDeleteSet,
  createSet as thunkCreateSet,
  updateSet as thunkUpdateSet,
  // selection + fields
  selectSet,
  fetchFields,
  fetchFieldDetail,
  openFieldModal,
  closeFieldModal,
  setEditingField,
  createField as thunkCreateField,
  updateField as thunkUpdateField,
  deleteField as thunkDeleteField,
  // descriptors list/search
  setDescPage,
  setDescPageSize,
  setDescSearch,
  fetchDescriptors,
  // descriptor modal + crud
  openDescriptorModal,
  closeDescriptorModal,
  setEditingDescriptor,
  createDescriptor as thunkCreateDescriptor,
  updateDescriptor as thunkUpdateDescriptor,
  deleteDescriptor as thunkDeleteDescriptor,
  openDeleteDescriptor,
  closeDeleteDescriptor,
  fetchFieldOptions,
  openDeleteField,
  closeDeleteField,
  DescriptorRow,
  openLinkedMediaModal,
  fetchDescriptorById,
} from "./descriptors.slice";
import LinkedMediaModal from "./LinkedMediaModal";
import { getUserRole } from "../../../hooks/getUserRoles";
import { post } from "../../../utils/apiUtils";
import { useTranslation } from "react-i18next";

const FIELD_COL_WIDTH = 220;
const CARD_HEIGHT = 329;

/* llogarit automatikisht scroll.y brenda kartës */
function useBodyY(
  cardRef: React.RefObject<HTMLDivElement>,
  headerRef: React.RefObject<HTMLDivElement>,
  fixedH = CARD_HEIGHT,
) {
  const [y, setY] = useState(200);
  useEffect(() => {
    const card = cardRef.current;
    const head = headerRef.current;
    if (!card || !head) return;

    const compute = () => {
      const cs = getComputedStyle(card);
      const pad =
        parseFloat(cs.paddingTop || "0") + parseFloat(cs.paddingBottom || "0");
      const headerH = head.offsetHeight || 0;
      setY(Math.max(80, fixedH - headerH - pad));
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(card);
    ro.observe(head);
    return () => ro.disconnect();
  }, [cardRef, headerRef, fixedH]);

  return y;
}

const Descriptors: React.FC = () => {
  const { t } = useTranslation("docProcessingDescriptors");
  const typeLabel = (v: string) => t(`fieldTypes.${v === "option_list" ? "optionList" : v}`, v);
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();
  const userRole = getUserRole();

  const { sets, fields, descriptors, modals, submitting, selectedSetId } =
    useAppSelector((s) => s.descriptors);

  const selectedSet = useMemo(
    () => sets.items.find((s) => s.id === selectedSetId) || null,
    [sets.items, selectedSetId],
  );

  // EXPORT hook
  const { exporting, exportNow } = useExporter({
    endpoint: "descriptors/export",
    fallbackPrefix: (selectedSet?.name || "descriptors").replace(
      /[^\w\d\-]+/g,
      "_",
    ),
    ext: "json",
  });

  // ----------------------- IMPORT state & handlers -----------------------
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClickImport = () => {
    if (!selectedSetId) {
      message.info(t("common.selectSetFirst"));
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset që të mund të zgjedhësh të njëjtin file prapë
    if (!file) return;

    if (!projectId || !selectedSetId) {
      message.info(t("common.selectSetFirst"));
      return;
    }

    try {
      setImporting(true);

      const fd = new FormData();
      fd.append("file", file);

      // Merr token nëse e ruan në localStorage; nëse ke axios instance, zëvendëso me të.
      const token = localStorage.getItem("token");

      const qs = new URLSearchParams({
        project_id: projectId,
        descriptor_set_id: selectedSetId,
        // ose përdor descriptor_set_name nëse s’zgjedh set-in:
        // descriptor_set_name: selectedSet?.name ?? ""
      });

      const res = await fetch(`/descriptors/import?${qs.toString()}`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          accept: "application/json",
          // mos vendos Content-Type për FormData (browser e shton boundary)
        } as any,
        body: fd,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const data = await res.json();
      message.success(data?.message || t("descriptors.messages.importCompleted"));

      // rifresko pas importit
      dispatch(fetchFields(selectedSetId));
      dispatch(fetchDescriptors());
    } catch (err: any) {
      message.error(err?.message || t("descriptors.messages.importFailed"));
    } finally {
      setImporting(false);
    }
  };
  // ----------------------------------------------------------------------

  /* ----------------------------- Effects --------------------------------- */
  useEffect(() => {
    dispatch(setProjectId(projectId ?? null));
  }, [projectId, dispatch]);

  useEffect(() => {
    if (projectId) dispatch(fetchSets());
  }, [projectId, dispatch]);

  useEffect(() => {
    if (selectedSetId) dispatch(fetchDescriptors());
  }, [
    selectedSetId,
    descriptors.page,
    descriptors.pageSize,
    descriptors.search,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedSetId && fields.items.length) {
      dispatch(fetchFieldOptions());
    }
  }, [dispatch, selectedSetId, fields.items.length]);

  /* ----------------------------- Handlers -------------------------------- */
  const handleSelectSet = useCallback(
    (row: { id: string }) => {
      dispatch(selectSet(row.id));
      dispatch(fetchFields(row.id));
      dispatch(setDescPage(1));
    },
    [dispatch],
  );

  const searchDebounce = useRef<number | null>(null);
  const handleSearch = useCallback(
    (v: string) => {
      if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
      searchDebounce.current = window.setTimeout(() => {
        dispatch(setDescSearch(v || ""));
        dispatch(setDescPage(1));
      }, 300);
    },
    [dispatch],
  );

  // Export descriptors
  const handleExportDescriptors = useCallback(async () => {
    if (!selectedSetId) {
      message.info(t("common.selectSetFirst"));
      return;
    }
    await exportNow({ params: { descriptor_set_id: selectedSetId } });
  }, [exportNow, selectedSetId]);

  /* ----------------------------- Columns --------------------------------- */
  const setColumns: ColumnsType<any> = useMemo(
    () => [
      { title: t("descriptors.sets.columnName"), dataIndex: "name", key: "name" },
      {
        title: "",
        key: "act",
        width: 90,
        fixed: "right",
        render: (_: any, row: any) => (
          <>
            {userRole !== "Technical Expert" && (
              <Space>
                <Tooltip title={t("descriptors.actions.edit")}>
                  <EditOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setEditingSet(row));
                      dispatch(openSetModal("edit"));
                    }}
                    style={{ color: "#A072EB", cursor: "pointer" }}
                  />
                </Tooltip>
                <Tooltip title={t("descriptors.actions.delete")}>
                  <DeleteOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(openDeleteSet(row));
                    }}
                    style={{ color: "#A072EB", cursor: "pointer" }}
                  />
                </Tooltip>
              </Space>
            )}
          </>
        ),
      },
    ],
    [dispatch, t, userRole],
  );

  const fieldColumns: ColumnsType<any> = useMemo(
    () => [
      {
        title: t("descriptors.fields.columnName"),
        dataIndex: "name",
        key: "name",
        width: 260,
        ellipsis: true,
      },
      {
        title: t("descriptors.fields.columnType"),
        dataIndex: "type",
        key: "type",
        width: 140,
        render: (v: string) => typeLabel(v),
      },
      {
        title: t("descriptors.fields.columnDynamic"),
        dataIndex: "is_dynamic",
        key: "is_dynamic",
        width: 120,
        render: (v: boolean) => (v ? t("common.yes") : t("common.no")),
      },
      {
        title: "",
        key: "act",
        width: 90,
        fixed: "right" as const,
        render: (_: any, row: any) => (
          <>
            {userRole !== "Technical Expert" && (
              <Space>
                <Tooltip title={t("descriptors.actions.edit")}>
                  <EditOutlined
                    onClick={() => {
                      dispatch(setEditingField(row));
                      dispatch(openFieldModal("edit"));
                      dispatch(fetchFieldDetail(row.id));
                    }}
                    style={{ color: "#A072EB", cursor: "pointer" }}
                  />
                </Tooltip>
                <Tooltip title={t("descriptors.actions.delete")}>
                  <DeleteOutlined
                    onClick={() => {
                      dispatch(openDeleteField({ id: row.id, name: row.name }));
                    }}
                    style={{ color: "#A072EB", cursor: "pointer" }}
                  />
                </Tooltip>
              </Space>
            )}
          </>
        ),
      },
    ],
    [dispatch, t, userRole],
  );

  const descriptorDynamicColumns: ColumnsType<any> = useMemo(
    () =>
      fields.items.map((f) => ({
        title: f.name,
        key: `field_${f.id}`,
        dataIndex: ["values", f.id],
        width: FIELD_COL_WIDTH,
        ellipsis: { showTitle: false },
        render: (val: any) => {
          let v = val ?? "";
          if (Array.isArray(v)) v = v.join(", ");
          else if (typeof v === "object" && v)
            v = v.label ?? v.value ?? JSON.stringify(v);
          else if (f.type === "date" && v) {
            try {
              v = new Date(v).toLocaleDateString();
            } catch {}
          }
          return <Tooltip title={v}>{v}</Tooltip>;
        },
      })),
    [fields.items],
  );

  const togglePromoteDescriptor = useCallback(
    async (row: any) => {
      const hasWeight = row.weight !== null && row.weight !== undefined;

      if (!hasWeight) return;

      try {
        const res = await post<any>(`/descriptors/${row.id}/promote`, {}, {});

        if (res?.type === "error") {
          throw new Error(res?.data?.message || "Failed");
        }

        message.success(
          row.is_promoted
            ? t("descriptors.messages.descriptorUnpromoted")
            : t("descriptors.messages.descriptorPromoted"),
        );

        dispatch(fetchDescriptors());
      } catch (e: any) {
        message.error(e?.message || t("descriptors.messages.promotionUpdateFailed"));
      }
    },
    [dispatch, t],
  );

  const descriptorActionsColumn: ColumnsType<any>[number] = useMemo(
    () => ({
      title: "",
      key: "act",
      fixed: "right",
      width: 120,
      render: (_: any, row: any) => {
        const hasWeight = row.weight !== null && row.weight !== undefined;
        const isPromoted = row.is_promoted === true;

        return (
          <>
            {userRole !== "Technical Expert" && (
              <Space>
                <Tooltip title={t("descriptors.actions.editDescriptor")}>
                  <EditOutlined
                    onClick={async () => {
                      dispatch(setEditingDescriptor(row));
                      try {
                        await dispatch(fetchDescriptorById(row.id)).unwrap();
                      } catch {}
                      dispatch(openDescriptorModal("edit"));
                    }}
                    style={{ color: "#A072EB", cursor: "pointer" }}
                  />
                </Tooltip>

                <Tooltip
                  title={
                    hasWeight
                      ? isPromoted
                        ? t("descriptors.actions.unpromoteDescriptor")
                        : t("descriptors.actions.promoteDescriptor")
                      : t("descriptors.actions.addWeightToPromote")
                  }
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePromoteDescriptor(row);
                    }}
                    style={{
                      cursor: hasWeight ? "pointer" : "not-allowed",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    {isPromoted ? (
                      <StarFilled
                        style={{
                          color: hasWeight ? "#A072EB" : "#E6D6FF",
                          fontSize: 16,
                          opacity: hasWeight ? 1 : 0.45,
                        }}
                      />
                    ) : (
                      <StarOutlined
                        style={{
                          color: hasWeight ? "#A072EB" : "#E6D6FF",
                          fontSize: 16,
                          opacity: hasWeight ? 1 : 0.45,
                        }}
                      />
                    )}
                  </span>
                </Tooltip>

                <Tooltip title={t("descriptors.actions.deleteDescriptor")}>
                  <DeleteOutlined
                    onClick={() => {
                      dispatch(
                        openDeleteDescriptor({
                          id: row.id,
                          title: row.media_titles || t("descriptors.actions.genericDescriptorLabel"),
                        }),
                      );
                    }}
                    style={{ color: "#A072EB", cursor: "pointer" }}
                  />
                </Tooltip>
              </Space>
            )}
          </>
        );
      },
    }),
    [dispatch, userRole, togglePromoteDescriptor, t],
  );

  const descriptorColumns: ColumnsType<any> = useMemo(
    () => [
      {
        title: t("descriptors.descriptorsSection.columnLinkedMedia"),
        dataIndex: "linked_media",
        key: "linked_media",
        width: 120,
        fixed: "left",
        render: (count: number, row: any) => (
          <Button
            size="small"
            style={{ border: "1px solid #722ED1", color: "#722ED1" }}
            onClick={() =>
              dispatch(
                openLinkedMediaModal({
                  descriptorId: row.id,
                  projectId: projectId || "",
                  label: row.media_titles || undefined,
                }),
              )
            }
          >
            {count ?? 0}
          </Button>
        ),
      },
      {
        title: t("descriptors.descriptorsSection.columnMediaTitles"),
        dataIndex: "media_titles",
        key: "media_titles",
        width: 260,
        fixed: "left",
        ellipsis: { showTitle: false },
        render: (v: any) => <Tooltip title={v || ""}>{v || ""}</Tooltip>,
      },
      {
        title: t("descriptors.descriptorsSection.columnWeight"),
        dataIndex: "weight",
        key: "weight",
        width: 120,
        render: (weight: number | null) =>
          weight !== null && weight !== undefined ? weight : "-",
      },
      ...descriptorDynamicColumns,
      descriptorActionsColumn,
    ],
    [descriptorDynamicColumns, descriptorActionsColumn, dispatch, projectId, t],
  );

  const scrollX = useMemo(() => {
    const dynamicWidth = fields.items.length * FIELD_COL_WIDTH;
    return Math.max(900, 120 + 260 + dynamicWidth + 90 + 80);
  }, [fields.items.length]);

  // refs për llogaritjen e height-it
  const setsCardRef = useRef<HTMLDivElement>(null);
  const setsHeaderRef = useRef<HTMLDivElement>(null);
  const fieldsCardRef = useRef<HTMLDivElement>(null);
  const fieldsHeaderRef = useRef<HTMLDivElement>(null);

  const setsTableY = useBodyY(setsCardRef, setsHeaderRef);
  const fieldsTableY = useBodyY(fieldsCardRef, fieldsHeaderRef);

  const canEditDescriptor =
    modals.descriptorMode === "edit" && !!modals.editingDescriptor;
  const editingDescriptor = modals.editingDescriptor;

  return (
    <div className="descriptor-container card">
      <h1>{t("descriptors.title")}</h1>

      <div className="descriptor-content">
        {/* Left: Sets */}
        <div ref={setsCardRef} className="desc-sets card">
          <div ref={setsHeaderRef} className="desc-sets-header">
            <h2>{t("descriptors.sets.header")}</h2>
            {userRole !== "Technical Expert" && (
              <div className="right">
                <img
                  src={PlusIcon}
                  alt="plus"
                  onClick={() => {
                    dispatch(setEditingSet(null));
                    dispatch(openSetModal("add"));
                  }}
                />
              </div>
            )}
          </div>

          <div className="card-body">
            <GenericTable
              className="sets-table clickable-rows"
              columns={setColumns}
              dataSource={sets.items}
              loading={sets.loading}
              emptyText={t("descriptors.sets.emptyText")}
              pagination={false}
              rowKey={(r) => r.id}
              scroll={{ y: setsTableY }}
              onRowClick={handleSelectSet}
              rowClassName={(row) =>
                row.id === selectedSetId ? "is-selected" : ""
              }
            />
          </div>
        </div>

        {/* Right: Fields */}
        <div ref={fieldsCardRef} className="set-fields card">
          <div ref={fieldsHeaderRef} className="set-fields-header">
            <h2>{t("descriptors.fields.header")}</h2>
            {userRole !== "Technical Expert" && (
              <Tooltip title={selectedSet ? t("descriptors.fields.addFieldTooltip") : t("descriptors.fields.selectSetFirstTooltip")}>
                <span
                  onClick={() => {
                    if (!selectedSet) return message.info(t("common.selectSetFirst"));
                    dispatch(openFieldModal("add"));
                  }}
                >
                  <img src={PlusIcon} alt="plus" />
                </span>
              </Tooltip>
            )}
          </div>

          <div className="card-body">
            {selectedSet ? (
              <GenericTable
                className="fields-table clickable-rows"
                columns={fieldColumns}
                dataSource={fields.items}
                loading={fields.loading}
                emptyText={t("descriptors.fields.emptyText")}
                pagination={false}
                rowKey={(r) => r.id}
                scroll={{ y: fieldsTableY }}
              />
            ) : (
              <div className="empty-fields">{t("descriptors.fields.selectSetPrompt")}</div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom: Descriptors */}
      <div className="descriptors-in-set card" style={{ marginTop: 16 }}>
        <div className="descriptors-header">
          <h2>{t("descriptors.descriptorsSection.header")}</h2>
          <div className="descriptors-table-actions">
            <Input.Search
              allowClear
              placeholder={t("descriptors.descriptorsSection.searchPlaceholder")}
              className="descriptor-search-input"
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
            />

            {/* Hidden file input për import */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />

            <Tooltip
              title={
                selectedSetId
                  ? t("descriptors.descriptorsSection.importTooltip")
                  : t("descriptors.descriptorsSection.selectSetFirstTooltip")
              }
            >
              <span>
                <Button
                  type="default"
                  icon={<ImportOutlined />}
                  onClick={handleClickImport}
                  disabled={!selectedSetId}
                  loading={importing}
                  style={{
                    border: "1px solid #A072EB",
                    background: "#A072EB",
                    color: "white",
                    height: "44px",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                  }}
                >
                  {t("descriptors.descriptorsSection.importButton")}
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                selectedSetId
                  ? t("descriptors.descriptorsSection.exportTooltip")
                  : t("descriptors.descriptorsSection.selectSetFirstTooltip")
              }
            >
              <span>
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={handleExportDescriptors}
                  disabled={!selectedSetId}
                  loading={exporting}
                  style={{
                    border: "1px solid #4A1C96",
                    background: "#4A1C96",
                    color: "white",
                    height: "44px",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                  }}
                >
                  {t("descriptors.descriptorsSection.exportButton")}
                </Button>
              </span>
            </Tooltip>

            {userRole !== "Technical Expert" && (
              <Tooltip
                title={
                  selectedSet
                    ? t("descriptors.descriptorsSection.addDescriptorTooltip")
                    : t("descriptors.descriptorsSection.selectSetFirstTooltip")
                }
              >
                <span
                  style={{ marginBottom: "unset" }}
                  onClick={() => {
                    if (!selectedSet) return message.info(t("common.selectSetFirst"));
                    dispatch(openDescriptorModal("add"));
                  }}
                >
                  <img src={PlusIcon} alt="plus" />
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {selectedSet ? (
          <GenericTable
            className="descriptors-table sticky-x clickable-rows"
            columns={descriptorColumns}
            dataSource={descriptors.items}
            loading={descriptors.loading}
            emptyText={t("descriptors.descriptorsSection.emptyText")}
            pagination={{
              current: descriptors.page,
              pageSize: descriptors.pageSize,
              total: descriptors.total,
              onChange: (p: number, ps: number) => {
                dispatch(setDescPage(p));
                dispatch(setDescPageSize(ps));
              },
            }}
            scroll={{ x: scrollX, y: 420 }}
            rowKey={(r) => r.id}
          />
        ) : (
          <div className="empty-fields">{t("descriptors.descriptorsSection.selectSetPrompt")}</div>
        )}
      </div>

      {/* --- Modals --- */}
      <AddDescriptorSetModal
        open={modals.setOpen}
        loading={submitting}
        onCancel={() => dispatch(closeSetModal())}
        onAdd={
          modals.setMode === "add"
            ? async (v) => {
                try {
                  await dispatch(thunkCreateSet(v.title)).unwrap();
                  message.success(t("descriptors.messages.setCreated"));
                } catch (e: any) {
                  message.error(e?.message || t("descriptors.messages.setCreateFailed"));
                }
              }
            : undefined
        }
        onEdit={
          modals.setMode === "edit" && modals.editingSet
            ? async (v) => {
                try {
                  await dispatch(
                    thunkUpdateSet({
                      id: modals.editingSet!.id,
                      title: v.title,
                    }),
                  ).unwrap();
                  message.success(t("descriptors.messages.setUpdated"));
                } catch (e: any) {
                  message.error(e?.message || t("descriptors.messages.setUpdateFailed"));
                }
              }
            : undefined
        }
        mode={modals.setMode}
        initialTitle={modals.editingSet?.name}
        className="add-descriptor-set-modal"
      />

      <AppModal
        open={modals.deleteSetOpen}
        onCancel={() => dispatch(closeDeleteSet())}
        onConfirm={async () => {
          if (!modals.deletingSet) return;
          try {
            await dispatch(thunkDeleteSet(modals.deletingSet.id)).unwrap();
            message.success(t("descriptors.messages.setDeleted"));
          } catch (e: any) {
            message.error(e?.message || t("descriptors.messages.setDeleteFailed"));
          }
        }}
        title={t("descriptors.deleteSetModal.title")}
        description={
          modals.deletingSet
            ? t("descriptors.deleteSetModal.description", { name: modals.deletingSet.name })
            : ""
        }
        confirmText={t("descriptors.deleteSetModal.confirmText")}
        confirmType="primary"
        danger
        showCancel
        showConfirm
        className="delete-descriptor-set-modal"
      />

      <AppModal
        open={modals.deleteFieldOpen}
        onCancel={() => dispatch(closeDeleteField())}
        onConfirm={async () => {
          if (!modals.deletingField) return;
          try {
            await dispatch(thunkDeleteField(modals.deletingField.id)).unwrap();
            message.success(t("descriptors.messages.fieldDeleted"));
          } catch (e: any) {
            message.error(e?.message || t("descriptors.messages.fieldDeleteFailed"));
          } finally {
            dispatch(closeDeleteField());
          }
        }}
        title={t("descriptors.deleteFieldModal.title")}
        description={
          modals.deletingField
            ? t("descriptors.deleteFieldModal.description", { name: modals.deletingField.name })
            : ""
        }
        confirmText={t("descriptors.deleteFieldModal.confirmText")}
        confirmType="primary"
        danger
        showCancel
        showConfirm
        className="delete-descriptor-set-modal"
      />

      <AddFieldModal
        open={modals.fieldOpen}
        loading={submitting || fields.loading}
        onCancel={() => dispatch(closeFieldModal())}
        onAdd={
          modals.fieldMode === "add"
            ? async (vals) => {
                try {
                  await dispatch(thunkCreateField(vals)).unwrap();
                  message.success(t("descriptors.messages.fieldAdded"));
                } catch (e: any) {
                  message.error(e?.message || t("descriptors.messages.fieldAddFailed"));
                }
              }
            : undefined
        }
        onEdit={
          modals.fieldMode === "edit" && modals.editingField
            ? async (vals) => {
                try {
                  await dispatch(
                    thunkUpdateField({ id: modals.editingField!.id, ...vals }),
                  ).unwrap();
                  message.success(t("descriptors.messages.fieldUpdated"));
                } catch (e: any) {
                  message.error(e?.message || t("descriptors.messages.fieldUpdateFailed"));
                }
              }
            : undefined
        }
        mode={modals.fieldMode}
        initialValues={modals.fieldInitialValues || undefined}
        selectedSet={
          selectedSet ? { id: selectedSet.id, name: selectedSet.name } : null
        }
        className="add-field-modal"
      />

      <AddDescriptorModal
        open={!!modals.descriptorOpen}
        loading={submitting}
        mode={modals.descriptorMode || "add"}
        onCancel={() => dispatch(closeDescriptorModal())}
        fields={fields.items}
        initialValues={
          modals.descriptorMode === "edit"
            ? modals.descriptorInitialValues || {}
            : undefined
        }
        mediaTitle={modals.descriptorInitialLabel || undefined}
        onAdd={async (vals) => {
          try {
            await dispatch(thunkCreateDescriptor(vals)).unwrap();
            message.success(t("descriptors.messages.descriptorAdded"));
          } catch (e: any) {
            message.error(e?.message || t("descriptors.messages.descriptorAddFailed"));
          }
        }}
        onEdit={
          modals.descriptorMode === "edit" && !!modals.editingDescriptor
            ? async (vals) => {
                const ed = modals.editingDescriptor as DescriptorRow;
                try {
                  await dispatch(
                    thunkUpdateDescriptor({ id: ed.id, formValues: vals }),
                  ).unwrap();
                  message.success(t("descriptors.messages.descriptorUpdated"));
                } catch (e: any) {
                  message.error(e?.message || t("descriptors.messages.descriptorUpdateFailed"));
                }
              }
            : undefined
        }
        className="add-field-modal"
      />

      <AppModal
        open={modals.deleteDescriptorOpen}
        onCancel={() => dispatch(closeDeleteDescriptor())}
        onConfirm={async () => {
          if (!modals.deletingDescriptor) return;
          try {
            await dispatch(
              thunkDeleteDescriptor(modals.deletingDescriptor.id),
            ).unwrap();
            message.success(t("descriptors.messages.descriptorDeleted"));
          } catch (e: any) {
            message.error(e?.message || t("descriptors.messages.descriptorDeleteFailed"));
          }
        }}
        title={t("descriptors.deleteDescriptorModal.title")}
        description={
          modals.deletingDescriptor
            ? t("descriptors.deleteDescriptorModal.description", {
                title:
                  modals.deletingDescriptor.title ||
                  t("descriptors.deleteDescriptorModal.defaultTitle"),
              })
            : ""
        }
        confirmText={t("descriptors.deleteDescriptorModal.confirmText")}
        confirmType="primary"
        danger
        showCancel
        showConfirm
        className="delete-descriptor-set-modal"
      />
      <LinkedMediaModal />
    </div>
  );
};

export default Descriptors;
